import BrandLogo from "@/components/BrandLogo";
import { useState } from "react";
import { useAuth } from "@/lib/AuthContext";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, AlertCircle, Info, ArrowLeft, CheckCircle2, KeyRound } from "lucide-react";
import BiometricQuickUnlock from "@/components/BiometricQuickUnlock";

function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function Login() {
  const { login, register } = useAuth();
  const [mode, setMode] = useState("login"); // login | register | forgot | reset
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [resetCode, setResetCode] = useState("");
  const [phone, setPhone] = useState("");
  const [debugCode, setDebugCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try { await login(email, password); }
    catch (err) { setError(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setLoading(false); }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try { await register(email, password, fullName, phone); }
    catch (err) { setError(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setLoading(false); }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true); setError(""); setDebugCode("");
    try {
      const { data } = await API.post("/auth/forgot-password", { email });
      setSuccess(data.message);
      if (data.debug_code) setDebugCode(data.debug_code);
      setMode("reset");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      const { data } = await API.post("/auth/reset-password", { email, code: resetCode, new_password: newPassword });
      setSuccess(data.message);
      setTimeout(() => { setMode("login"); setSuccess(""); setResetCode(""); setNewPassword(""); setDebugCode(""); }, 2000);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center px-4">
      <div className="w-full max-w-sm space-y-6">
        {/* Logo */}
        <div className="flex flex-col items-center gap-4 text-center">
          <BrandLogo className="w-64 max-w-full" testId="login-logo" />
          <p className="text-sm text-muted-foreground">Care that meets the ADHD brain where it is.</p>
        </div>

        {/* Register hint */}
        {mode === "register" && (
          <div className="bg-accent border border-primary/10 rounded-xl p-4 flex gap-3" data-testid="intakeq-email-hint">
            <Info className="w-5 h-5 text-primary shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-foreground">Use your IntakeQ email</p>
              <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">Please register with the same email you used when signing up with Elevated Psychiatry.</p>
            </div>
          </div>
        )}

        {/* Success message */}
        {success && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex gap-2" data-testid="success-message">
            <CheckCircle2 className="w-4 h-4 text-green-600 shrink-0 mt-0.5" />
            <p className="text-sm text-green-700">{success}</p>
          </div>
        )}

        {/* LOGIN MODE */}
        {mode === "login" && (
          <>
            <BiometricQuickUnlock />
            <form onSubmit={handleLogin} className="space-y-4" data-testid="auth-form">
            <div>
              <Label className="text-sm font-medium">Email</Label>
              <Input data-testid="auth-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="mt-1.5 h-12" required />
            </div>
            <div>
              <Label className="text-sm font-medium">Password</Label>
              <Input data-testid="auth-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Enter your password" className="mt-1.5 h-12" required />
            </div>
            {error && <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex gap-2" data-testid="auth-error"><AlertCircle className="w-4 h-4 text-red-600 shrink-0 mt-0.5" /><p className="text-sm text-red-700">{error}</p></div>}
            <Button data-testid="auth-submit-button" type="submit" className="w-full h-12 rounded-full text-base" disabled={loading}>
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Sign In"}
            </Button>
            <div className="flex items-center justify-between">
              <button type="button" data-testid="forgot-password-link" onClick={() => { setMode("forgot"); setError(""); setSuccess(""); }} className="text-sm text-primary font-medium hover:underline">Forgot password?</button>
              <button type="button" data-testid="auth-toggle-button" onClick={() => { setMode("register"); setError(""); }} className="text-sm text-primary font-medium hover:underline">Register</button>
            </div>
          </form>
          </>
        )}

        {/* REGISTER MODE */}
        {mode === "register" && (
          <form onSubmit={handleRegister} className="space-y-4" data-testid="auth-form">
            <div>
              <Label className="text-sm font-medium">Full Name</Label>
              <Input data-testid="auth-fullname-input" type="text" value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder="Your full name" className="mt-1.5 h-12" required />
            </div>
            <div>
              <Label className="text-sm font-medium">Email (same as your IntakeQ email)</Label>
              <Input data-testid="auth-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="your-intakeq-email@example.com" className="mt-1.5 h-12" required />
            </div>
            <div>
              <Label className="text-sm font-medium">Mobile phone</Label>
              <Input data-testid="auth-phone-input" type="tel" inputMode="tel" autoComplete="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="(555) 555-1234" className="mt-1.5 h-12" required />
              <p className="text-[11px] text-muted-foreground mt-1.5">We use this so your provider can reach you by SMS for replies, refill updates, and reminders.</p>
            </div>
            <div>
              <Label className="text-sm font-medium">Password</Label>
              <Input data-testid="auth-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Create a password" className="mt-1.5 h-12" required />
            </div>
            {error && <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex gap-2" data-testid="auth-error"><AlertCircle className="w-4 h-4 text-red-600 shrink-0 mt-0.5" /><p className="text-sm text-red-700">{error}</p></div>}
            <Button data-testid="auth-submit-button" type="submit" className="w-full h-12 rounded-full text-base" disabled={loading}>
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Create Account"}
            </Button>
            <button type="button" data-testid="auth-toggle-button" onClick={() => { setMode("login"); setError(""); }} className="text-sm text-primary font-medium hover:underline w-full text-center">Already have an account? Sign In</button>
          </form>
        )}

        {/* FORGOT PASSWORD MODE */}
        {mode === "forgot" && (
          <form onSubmit={handleForgotPassword} className="space-y-4" data-testid="forgot-form">
            <button type="button" onClick={() => { setMode("login"); setError(""); }} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft className="w-4 h-4" /> Back to Sign In</button>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-xl bg-accent flex items-center justify-center"><KeyRound className="w-5 h-5 text-primary" /></div>
              <div><h2 className="font-semibold text-lg">Reset Password</h2><p className="text-xs text-muted-foreground">Enter your email to receive a reset code</p></div>
            </div>
            <div>
              <Label className="text-sm font-medium">Email</Label>
              <Input data-testid="forgot-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="your@email.com" className="mt-1.5 h-12" required />
            </div>
            {error && <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex gap-2"><AlertCircle className="w-4 h-4 text-red-600 shrink-0 mt-0.5" /><p className="text-sm text-red-700">{error}</p></div>}
            <Button data-testid="send-reset-code-button" type="submit" className="w-full h-12 rounded-full text-base" disabled={loading}>
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Send Reset Code"}
            </Button>
          </form>
        )}

        {/* RESET PASSWORD MODE */}
        {mode === "reset" && (
          <form onSubmit={handleResetPassword} className="space-y-4" data-testid="reset-form">
            <button type="button" onClick={() => { setMode("forgot"); setError(""); setSuccess(""); }} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft className="w-4 h-4" /> Back</button>
            <div className="bg-accent rounded-xl p-4 text-sm text-foreground leading-relaxed">
              A 6-digit code was sent to <strong>{email}</strong>. Check your email and enter the code below with your new password.
            </div>

            {debugCode && (
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-center" data-testid="debug-reset-code">
                <p className="text-xs text-blue-600 font-medium uppercase tracking-widest mb-1">Your Reset Code</p>
                <p className="text-3xl font-bold text-blue-800 font-mono tracking-[0.3em]">{debugCode}</p>
                <p className="text-xs text-blue-500 mt-1">Email delivery will activate once your domain is verified in Resend</p>
              </div>
            )}

            <div>
              <Label className="text-sm font-medium">Reset Code</Label>
              <Input data-testid="reset-code-input" type="text" value={resetCode} onChange={(e) => setResetCode(e.target.value)} placeholder="123456" className="mt-1.5 h-12 text-center text-2xl font-mono tracking-[0.3em]" maxLength={6} required />
            </div>
            <div>
              <Label className="text-sm font-medium">New Password</Label>
              <Input data-testid="new-password-input" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="At least 6 characters" className="mt-1.5 h-12" minLength={6} required />
            </div>
            {error && <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex gap-2"><AlertCircle className="w-4 h-4 text-red-600 shrink-0 mt-0.5" /><p className="text-sm text-red-700">{error}</p></div>}
            <Button data-testid="reset-password-button" type="submit" className="w-full h-12 rounded-full text-base" disabled={loading || resetCode.length < 6}>
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Reset Password"}
            </Button>
          </form>
        )}

        {/* Demo credentials */}
        <div className="bg-accent rounded-xl p-4 text-center space-y-2">
          <p className="text-xs text-muted-foreground">Demo: <span className="font-medium">patient@demo.com</span> / <span className="font-medium">patient123</span></p>
          <a href="/provider-login" className="text-xs text-primary hover:underline font-medium inline-block" data-testid="provider-login-link">Are you a provider? Sign in here →</a>
        </div>
        <div className="text-center text-[11px] text-muted-foreground pt-2">
          <a href="/privacy" className="hover:text-foreground underline underline-offset-2" data-testid="privacy-policy-link">Privacy Policy</a>
        </div>
      </div>
    </div>
  );
}
